/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useRef } from 'react';
import { Stack } from '../../../design-system/layout/Stack';
import { ModuleConfigCard } from '../../organisms/ModuleConfigCard/ModuleConfigCard';
import { theme } from '../../../design-system/theme/theme';
import type { ModuleConfig } from '../../types';

/** 이 화면이 필요로 하는 데이터. 공급 방식은 미정 (현재 데모 데이터). */
export interface ModulesData {
  modules: ModuleConfig[];
}

export interface ModulesScreenProps {
  data: ModulesData;
  /** 진입 시 상단으로 스크롤할 모듈명 (편성표 모듈 클릭). null이면 스크롤 안 함. */
  focusModule?: string | null;
  /** 스크롤 처리 완료 알림 (한 번만 스크롤하도록 부모가 focusModule 초기화). */
  onFocusHandled?: () => void;
  onEdit?: (moduleName: string) => void;
  onRunNow?: (moduleName: string) => void;
  /** 모듈별 "지금 수집" 활성화 여부. 미지정 시 전부 비활성. */
  isRunNowEnabled?: (moduleName: string) => boolean;
  onToggleEnabled?: (moduleName: string, enabled: boolean) => void;
  onChangeCollector?: (moduleName: string, collectorType: string) => void;
}

const descStyle = css`
  margin: 0;
  font-size: ${theme.fontSize.md}px;
  color: ${theme.color.textMuted};
`;

/* 스크롤 이동 시 sticky 헤더(56px)에 가리지 않도록 상단 여백 확보. */
const cardWrapStyle = css`
  scroll-margin-top: ${56 + theme.space.md}px;
`;

/** 모듈 관리 화면. module_config 목록 표시. */
export function ModulesScreen({ data, focusModule, onFocusHandled, onEdit, onRunNow, isRunNowEnabled, onToggleEnabled, onChangeCollector }: ModulesScreenProps) {
  // 모듈명 → 카드 래퍼 DOM. focusModule 스크롤 대상 참조용.
  const cardRefs = useRef(new Map<string, HTMLDivElement>());

  // focusModule이 있으면 해당 카드를 스크롤 컨테이너 상단으로 이동시킨 뒤, 부모에 완료를 알린다.
  // 목록이 아직 안 그려졌으면(ref 없음) 다음 렌더에서 재시도. 완료 알림으로 한 번만 스크롤된다.
  useEffect(() => {
    if (!focusModule) return;
    const el = cardRefs.current.get(focusModule);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    onFocusHandled?.();
  }, [focusModule, data.modules, onFocusHandled]);

  return (
    <Stack gap="md">
      <p css={descStyle}>module_config 테이블 — 모듈별 스케줄·기본 입력·수집기 설정</p>
      {data.modules.map((m) => (
        <div
          key={m.name}
          css={cardWrapStyle}
          ref={(el) => {
            if (el) cardRefs.current.set(m.name, el);
            else cardRefs.current.delete(m.name);
          }}
        >
          <ModuleConfigCard
            module={m}
            onEdit={() => onEdit?.(m.name)}
            onRunNow={() => onRunNow?.(m.name)}
            runNowEnabled={isRunNowEnabled?.(m.name) ?? false}
            onToggleEnabled={(v) => onToggleEnabled?.(m.name, v)}
            onChangeCollector={(v) => onChangeCollector?.(m.name, v)}
          />
        </div>
      ))}
      {/* TODO: 수정 모달, 지금 수집 트리거, enabled·collector 변경 API 연결 */}
    </Stack>
  );
}
